import { useCallback, useEffect, useState } from 'react';

import type { InputField, Preset } from '@/constants/presets';

import { fieldOptions } from './kit';

export type FieldValues = Map<string, any>;

/** Reads a value written by react-use's useLocalStorage (JSON encoded). */
export function readStored<T = unknown>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? null : (JSON.parse(raw) as T);
  } catch {
    return null;
  }
}

/** Writes a value the way react-use's useLocalStorage does (JSON encoded). */
export function writeStored(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage may be unavailable (private mode, blocked storage)
  }
}

/**
 * A field's starting value. Anonymous runs default `length` to Short (they
 * are capped and cheap by design); a radio group starts on the option it
 * marks as checked, so what the visitor sees selected is what gets sent.
 */
export function defaultFieldValue(field: InputField) {
  if (field.initialValue !== undefined && field.initialValue !== '') {
    return field.initialValue;
  }
  if (field.name === 'length') return 'Short';
  if (field.type === 'radio') {
    return field.radioOptions?.find((option) => option.checked)?.name ?? '';
  }
  return '';
}

/** A saved value is only reused while the field could still show it. */
function savedValueFits(field: InputField, value: unknown) {
  if (value === undefined || value === null || value === '') return false;
  if (field.type === 'select' || field.type === 'radio') {
    return fieldOptions(field).includes(value as string);
  }
  if (field.type === 'file' || field.type === 'files') return false;
  return true;
}

const isUpload = (field?: InputField) =>
  field?.type === 'file' || field?.type === 'files';

/**
 * The template's field values. They persist under `preset-fields-<slug>`,
 * the key GenericComponent reads once after sign-up, so whatever a visitor
 * typed here is waiting for them in the tool.
 *
 * The first render uses the defaults, like the server render; saved values
 * load after mount, so hydration never sees a mismatch.
 */
export function useTemplateFields(preset: Preset) {
  const storageKey = `preset-fields-${preset.slug}`;
  const [hydrated, setHydrated] = useState(false);

  const [fields, setFields] = useState<FieldValues>(() => {
    const values: FieldValues = new Map();
    preset.fields?.forEach((field) =>
      values.set(field.name, defaultFieldValue(field)),
    );
    return values;
  });

  useEffect(() => {
    const saved = readStored<Record<string, unknown>>(storageKey);
    if (saved && typeof saved === 'object') {
      setFields((current) => {
        const next = new Map(current);
        preset.fields?.forEach((field) => {
          if (savedValueFits(field, saved[field.name])) {
            next.set(field.name, saved[field.name]);
          }
        });
        return next;
      });
    }
    setHydrated(true);
    // Load once per template.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  useEffect(() => {
    if (!hydrated) return;
    // Uploads are anonymous storage paths, not worth restoring later.
    writeStored(
      storageKey,
      Object.fromEntries(
        [...fields].filter(
          ([name]) =>
            !isUpload(preset.fields?.find((field) => field.name === name)),
        ),
      ),
    );
  }, [fields, hydrated, preset.fields, storageKey]);

  const setField = useCallback((name: string, value: any) => {
    setFields((current) => {
      const next = new Map(current);
      next.set(name, value);
      return next;
    });
  }, []);

  /** Empties text inputs; choices go back to their defaults. */
  const clearFields = useCallback(() => {
    setFields((current) => {
      const next: FieldValues = new Map();
      preset.fields?.forEach((field) => {
        next.set(
          field.name,
          field.type === 'checkbox'
            ? current.get(field.name)
            : field.type === 'select' || field.type === 'radio'
              ? defaultFieldValue(field)
              : '',
        );
      });
      return next;
    });
  }, [preset.fields]);

  return { fields, setField, clearFields, hydrated };
}
